import { GeneralStatus } from 'modules/status-badge';
import {
  Assignment,
  ReasonTimeMismatchType,
  WeekDetails,
  WeekDetailsContextState
} from '../../types';
import { autoCloseLabelMap } from '../../utils';

export const mockWeekId = 'mockWeekId';
export const mockReasonId = 'mockReasonId';
export const weekDetailsApiMock: WeekDetails = {
  data: {
    assignments: {
      [mockWeekId]: {
        companyName: 'companyName',
        reportedMinutes: 0,
        expectedMinutes: 0,
        reportedReasonMinutes: 0,
        weekStatus: GeneralStatus.CLOSED,
        weekAutoClosing: '2024-01-14T00:00:00.000Z',
        shifts: [],
        reasons: [mockReasonId],
        isGeofenceEnabled: false
      }
    },
    shifts: {
      shift1: {
        shiftStatus: GeneralStatus.IN_REVIEW,
        shiftReportedMinutes: 0,
        shiftStart: '2024-01-07T00:00:00.000Z'
      }
    },
    reasons: {
      [mockReasonId]: {
        message: 'message',
        reportedReasonMinutes: 0,
        timeMismatchType: ReasonTimeMismatchType.COMPANY_DECISION,
        status: GeneralStatus.IN_REVIEW
      }
    }
  }
};

export const weekDetailsContextMock: WeekDetailsContextState = {
  weekDetails: weekDetailsApiMock.data,
  isLoading: false,
  isError: false
};

export const durationMockList: { duration: Duration; label: string }[] = [
  {
    duration: {
      days: 1,
      hours: 3,
      minutes: 55
    },
    label: '1d 3h 55min'
  },
  {
    duration: {
      days: 0,
      hours: 3,
      minutes: 55
    },
    label: '3h 55min'
  },
  {
    duration: {
      days: 0,
      hours: 0,
      minutes: 55
    },
    label: '55min'
  },
  {
    duration: {
      days: 0,
      hours: 0,
      minutes: 0
    },
    label: '0min'
  },
  {
    duration: {
      days: 3,
      hours: 0,
      minutes: 0
    },
    label: '3d 0h 0min'
  },
  {
    duration: {
      days: 3,
      hours: 0,
      minutes: 55
    },
    label: '3d 0h 55min'
  }
];

export const getAutoCloseDataMockList: () => {
  data: {
    weekStatus: GeneralStatus;
    weekAutoClosing: string | undefined | null;
    now: Date;
  };
  label: string;
}[] = () => [
  // if we have weekStatus: 'CLOSED' we should display {autoCloseLabelMap.CLOSED}
  {
    data: {
      weekStatus: GeneralStatus.CLOSED,
      weekAutoClosing: '2023-10-10T00:00:00.000Z',
      now: new Date('2023-10-10T00:00:00.000Z')
    },
    label: autoCloseLabelMap.CLOSED()
  },
  // if we have weekStatus: 'OPEN' we should display {autoCloseLabelMap.OPEN} following with the correct duration label
  {
    data: {
      weekStatus: GeneralStatus.OPEN,
      weekAutoClosing: '2023-10-15T00:00:00.000Z',
      now: new Date('2023-10-10T00:00:00.000Z')
    },
    label: `${autoCloseLabelMap.OPEN()}: 5d 0h 0min`
  },
  // if we have weekAutoClosing undefined for any reason, we should not display the autoCloseLabel
  {
    data: {
      weekStatus: GeneralStatus.OPEN,
      weekAutoClosing: undefined,
      now: new Date('2023-10-10T00:00:00.000Z')
    },
    label: ''
  },
  // if we have weekAutoClosing that is past date for any reason, we should not display the autoCloseLabel
  {
    data: {
      weekStatus: GeneralStatus.OPEN,
      weekAutoClosing: '2023-10-09T00:00:00.000Z',
      now: new Date('2023-10-10T00:00:00.000Z')
    },
    label: ''
  }
];

export const buttonsMockData: {
  props: Partial<Assignment>;
  closeWeekButton: {
    isInDocument: boolean;
    isDisabled?: boolean;
  };
  justifyDeviationButton: {
    isInDocument: boolean;
    isDisabled?: boolean;
    isContained?: boolean;
  };
}[] = [
  // Both are not rendered if weekStatus: WeekStatus.CLOSED
  {
    props: {
      weekStatus: GeneralStatus.CLOSED,
      reportedReasonMinutes: 0,
      reportedMinutes: 600,
      expectedMinutes: 600
    },
    closeWeekButton: {
      isInDocument: false
    },
    justifyDeviationButton: {
      isInDocument: false
    }
  },
  // reportedMinutes < expectedMinutes and reportedReasonMinutes === 0
  // closeWeekButton is disabled
  // justifyDeviationButton is outlined enabled
  {
    props: {
      weekStatus: GeneralStatus.OPEN,
      reportedReasonMinutes: 0,
      reportedMinutes: 500,
      expectedMinutes: 600
    },
    closeWeekButton: {
      isInDocument: true,
      isDisabled: true
    },
    justifyDeviationButton: {
      isInDocument: true,
      isDisabled: false,
      isContained: false
    }
  },
  // reportedMinutes > expectedMinutes and reportedReasonMinutes === 0 (overtime)
  // closeWeekButton is disabled
  // justifyDeviationButton is contained enabled
  {
    props: {
      weekStatus: GeneralStatus.OPEN,
      reportedReasonMinutes: 0,
      reportedMinutes: 600,
      expectedMinutes: 500
    },
    closeWeekButton: {
      isInDocument: true,
      isDisabled: true
    },
    justifyDeviationButton: {
      isInDocument: true,
      isDisabled: false,
      isContained: true
    }
  },
  // expectedMinutes < reportedMinutes + reportedReasonMinutes (overtime not fully covered with reasons)
  // closeWeekButton is disabled
  // justifyDeviationButton is contained enabled
  {
    props: {
      weekStatus: GeneralStatus.OPEN,
      reportedReasonMinutes: -30,
      reportedMinutes: 600,
      expectedMinutes: 500
    },
    closeWeekButton: {
      isInDocument: true,
      isDisabled: true
    },
    justifyDeviationButton: {
      isInDocument: true,
      isDisabled: false,
      isContained: true
    }
  },
  // expectedMinutes === reportedMinutes + reportedReasonMinutes (overtime)
  // closeWeekButton is enabled
  // justifyDeviationButton is outlined disabled
  {
    props: {
      weekStatus: GeneralStatus.OPEN,
      reportedReasonMinutes: -100,
      reportedMinutes: 600,
      expectedMinutes: 500
    },
    closeWeekButton: {
      isInDocument: true,
      isDisabled: false
    },
    justifyDeviationButton: {
      isInDocument: true,
      isDisabled: true,
      isContained: false
    }
  },
  // expectedMinutes === reportedMinutes + reportedReasonMinutes
  // closeWeekButton is enabled
  // justifyDeviationButton is outlined disabled
  {
    props: {
      weekStatus: GeneralStatus.OPEN,
      reportedReasonMinutes: 100,
      reportedMinutes: 500,
      expectedMinutes: 600
    },
    closeWeekButton: {
      isInDocument: true,
      isDisabled: false
    },
    justifyDeviationButton: {
      isInDocument: true,
      isDisabled: true,
      isContained: false
    }
  },
  // expectedMinutes > reportedMinutes + reportedReasonMinutes (undertime not fully covered with reasons)
  // closeWeekButton is disabled
  // justifyDeviationButton is contained enabled
  {
    props: {
      weekStatus: GeneralStatus.OPEN,
      reportedReasonMinutes: 30,
      reportedMinutes: 500,
      expectedMinutes: 600
    },
    closeWeekButton: {
      isInDocument: true,
      isDisabled: true
    },
    justifyDeviationButton: {
      isInDocument: true,
      isDisabled: false,
      isContained: false
    }
  },
  // reportedMinutes === expectedMinutes and reportedReasonMinutes === 0
  // closeWeekButton is enabled
  // justifyDeviationButton is not rendered
  {
    props: {
      weekStatus: GeneralStatus.OPEN,
      reportedReasonMinutes: 0,
      reportedMinutes: 600,
      expectedMinutes: 600
    },
    closeWeekButton: {
      isInDocument: true,
      isDisabled: false
    },
    justifyDeviationButton: {
      isInDocument: false
    }
  }
];
